// Loading a month, moving between months, and looking inside one of its days.
//
// TWO READS, AND THEY ANSWER DIFFERENT QUESTIONS. `GET /months/:month` is the
// grid: how much is due on each of thirty-one days, and which schedules. `GET
// /days/:day` is what is actually on ONE of them, and it is the endpoint the day
// screen already uses - so a square and the screen behind it cannot disagree
// about what a day holds, because they are literally the same answer.
//
// WHICH MONTH IT IS is never this client's answer. `'this'` is what goes to the
// server when there is no day on screen to take a month off, exactly as the day
// screen asks for `'today'`, and the server resolves it in the user's own stored
// zone. Where the screen is opened from a day the server has already named, the
// month of THAT day is what is asked for - string work on the server's own value
// rather than a clock (`App.tsx`, at the control in the date header).
//
// Every move after that follows `previousMonth` / `nextMonth` off the payload
// while there is a payload to follow. A month that could NOT be read still has
// arrows, derived by `addMonths`, because the alternative is a failed month that
// takes away the only way back to one that loaded - see `previousMonth` below.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { DayResponse, IsoDay, IsoMonth, MonthResponse } from '../api/client.ts';
import { addMonths, parseIsoDay } from '../iso-day.ts';
import { monthOfDay } from './month-model.ts';

/** The literal the server resolves in the user's stored zone. */
export const THIS_MONTH = 'this';

export type MonthKey = IsoMonth | typeof THIS_MONTH;

type Entry<T> =
  | { status: 'loading' }
  /**
   * `refreshFailure` is set when a LATER read of the same thing could not be
   * made. The payload stays, because it is the answer somebody is looking at and
   * it was right when it arrived; the reason travels with it so the screen can
   * say which of the two it is showing.
   */
  | { status: 'loaded'; payload: T; refreshFailure?: string }
  | { status: 'failed'; message: string };

/**
 * A read that did not answer, filed against what is already there.
 *
 * A FAILURE MUST NOT DESTROY THE WORKING THING. Where nothing has ever loaded
 * this is the whole state - "That month is not here." and a way to ask again.
 * Where something HAS loaded, replacing it would throw away a correct grid
 * somebody is reading in order to avoid showing one that might be a number out
 * of date - and the rule this screen holds is a promise not to LIE rather than a
 * promise to be silent, so the payload stays and the reason is said out loud
 * beside it. That is the shape `SettingsScreen` already uses for its own list.
 */
function withProblem<T>(
  cache: Record<string, Entry<T>>,
  key: string,
  message: string,
): Record<string, Entry<T>> {
  const current = cache[key];
  if (current?.status === 'loaded') {
    return { ...cache, [key]: { ...current, refreshFailure: message } };
  }
  return { ...cache, [key]: { status: 'failed', message } };
}

/**
 * Which read a cache entry is the result of, and therefore when the effects
 * below may stop asking for it. ONE record answering both halves of the rule,
 * because two mechanisms for one invariant is how this drifted apart before.
 *
 * `revision` is the write round: anything read before the last create or edit is
 * stale whatever else is true of it.
 *
 * `completions` is the tick round. BOTH READS HERE ARE DERIVED FROM THE
 * COMPLETION LOG - a square's `done` and the panel's own ticks - so a tick made
 * on the day screen leaves them saying a number that is no longer true, and this
 * screen exists to agree with the day rather than to contradict it.
 *
 * `visit` is the trip to this screen the read was made on, and it is NULL for a
 * read that produced an ANSWER. An answer belongs to the write round and is as
 * good on the next trip as on this one, which is what keeps coming back to the
 * month free. A read that has NOT answered - one that failed, and one still out
 * when the screen was left - belongs to the trip that made it and to no other,
 * so leaving and returning asks again however late the failure lands.
 */
interface Round {
  revision: number;
  completions: number;
  visit: number | null;
}

/** Whether a recorded read still counts as this round's, so nothing is re-asked. */
function alreadyRead(record: Round | undefined, now: Round): boolean {
  if (record === undefined) return false;
  if (record.revision !== now.revision || record.completions !== now.completions) return false;
  return record.visit === null || record.visit === now.visit;
}

/**
 * Whether the payload answers the month it was asked for, and in a form this
 * screen can lay out.
 *
 * `firstDay` is the one field the grid cannot recover from: the blanks before
 * the 1st are its weekday, so a `date` column serialised as a timestamp would
 * shift every square in the month by some number of columns and still look like
 * a calendar. Reported rather than drawn, with the specifics going to whoever is
 * building the server - the day screen makes exactly this check for exactly this
 * reason.
 */
function contractProblem(payload: MonthResponse, requested: MonthKey): string | null {
  if (parseIsoDay(payload.firstDay) === null || payload.days.length === 0) {
    console.warn(
      `Perfect Note: the month payload starts at ${JSON.stringify(payload.firstDay)} and holds ${payload.days.length} days, which is not a month this screen can lay out.`,
    );
    return 'The server sent a month in a form this screen cannot read.';
  }
  if (requested !== THIS_MONTH && payload.month !== requested) {
    console.warn(
      `Perfect Note: asked for ${requested} and the server answered with ${payload.month}.`,
    );
    return 'The server sent a different month from the one this screen asked for.';
  }
  return null;
}

/**
 * Whether the day payload answers the day the panel asked for.
 *
 * The same check `contractProblem` makes above for a month, and `useDayScreen`
 * makes for the day screen's own read, for the same reason: the panel draws its
 * HEADING from the day that was asked for and its list from the payload, so a
 * server answering with another day would put that day's items under this day's
 * date and nothing on screen would say so.
 */
function dayContractProblem(payload: DayResponse, requested: IsoDay): string | null {
  if (payload.day !== requested) {
    console.warn(
      `Perfect Note: asked for ${requested} and the server answered with ${JSON.stringify(payload.day)}.`,
    );
    return 'The server sent a different day from the one this screen asked for.';
  }
  return null;
}

export interface MonthScreenState {
  /** The month being looked at, once it has loaded. */
  view: MonthResponse | null;
  /**
   * Which month is being ASKED FOR, which is a different question from which one
   * loaded: the bar names this one, so a September that could not be read never
   * sits under a heading still reading August.
   *
   * Null only before anything at all has come back, which is the one state where
   * `'this'` has not been resolved by any payload and there is therefore no month
   * to name - and no month already read for a failure to take away.
   */
  month: IsoMonth | null;
  /**
   * Where the arrows go. The PAYLOAD'S OWN whenever there is a payload, so the
   * server is the authority the moment it has spoken; `addMonths` off the month
   * being asked for when there is not, because a month that failed to arrive must
   * not remove the way back to the one before it. Null exactly when `month` is.
   */
  previousMonth: IsoMonth | null;
  nextMonth: IsoMonth | null;
  /**
   * The month the server's own today falls in, or null until some payload has
   * said which day that is. Never a clock here - `payload.today` is resolved in
   * the user's stored zone.
   */
  thisMonth: IsoMonth | null;
  /** Whether the month being looked at is on its way. */
  loading: boolean;
  /** Set only when the month being looked at could not be read AT ALL. */
  failure: string | null;
  /**
   * Set when the month ON SCREEN is still drawn but a later read of it could not
   * be made, so what is shown may be a tick or an edit out of date. Never both
   * this and `failure`: one is "there is nothing to show", the other is "this is
   * here, and here is what is uncertain about it".
   */
  refreshFailure: string | null;
  /**
   * Go to a month, or to the literal `'this'` and let the server say which that
   * is. Every caller passes either a month the SERVER named, a neighbour of one
   * (`addMonths`), or the literal - never a month worked out from a clock.
   */
  go: (month: MonthKey) => void;
  /** Ask for the month on screen again after it could not be read. */
  retry: () => void;
  /** The square that is open, or null when none is. */
  selected: IsoDay | null;
  /** Open a square, or close the one that is open by choosing it again. */
  select: (day: IsoDay | null) => void;
  /** What is on the selected day, once it has arrived. */
  day: DayResponse | null;
  dayLoading: boolean;
  dayFailure: string | null;
  /** The panel's own version of `refreshFailure`, and for the same reason. */
  dayRefreshFailure: string | null;
  retryDay: () => void;
}

export interface MonthScreenOptions {
  /**
   * Whether the month is the screen on. Nothing is fetched while it is not.
   *
   * The DAY is read above the screens and stays read, because coming back to
   * Today must cost nothing and it is the screen the app is opened on. The month
   * is not: reading it on every launch would be a request for a screen most
   * mornings nobody opens. The cache lives in this hook rather than in the
   * screen, so leaving the month and coming back still costs nothing - what is
   * deferred is the FIRST read, not every one after it.
   */
  active: boolean;
  /**
   * Bumped by every write the sheets make. A month read in an earlier round is
   * stale - shown while it is replaced rather than thrown away, because
   * emptying the cache would put "Loading…" over the whole grid every time
   * something is added. The day screen's own cache makes the same trade.
   */
  revision: number;
  /**
   * Bumped by every tick that reaches the server (`useDayScreen`). Watched
   * separately from `revision` because the day screen's own read must NOT be
   * re-sent on a tick - that would undo the optimistic update - while both reads
   * HERE are counts of completions and are wrong the moment one is written.
   */
  completions: number;
  /** Raised by any 401, from either read. Never lowered - see `useDayScreen`. */
  onSignedOut: () => void;
}

export function useMonthScreen(options: MonthScreenOptions): MonthScreenState {
  const { active, revision, completions, onSignedOut } = options;

  const [key, setKey] = useState<MonthKey>(THIS_MONTH);
  const [byMonth, setByMonth] = useState<Record<MonthKey, Entry<MonthResponse>>>({});
  const [selected, setSelected] = useState<IsoDay | null>(null);
  const [byDay, setByDay] = useState<Record<IsoDay, Entry<DayResponse>>>({});
  // The server's own today, kept from the last payload that carried one so the
  // bar can still say which month is THIS month while the month on screen has no
  // payload of its own. It is the server's answer either way: no clock is read
  // here, and a `today` that is not a calendar date is not remembered at all
  // rather than becoming a month name made of NaN.
  const [today, setToday] = useState<IsoDay | null>(null);

  // Read through a ref so the effects below are not re-run by a caller that
  // re-renders: `onSignedOut` is a fresh closure every render, and depending on
  // it would re-read the month on every keystroke anywhere in the shell.
  const signedOut = useRef(onSignedOut);
  signedOut.current = onSignedOut;

  // Which round of reads each entry belongs to, and which reads are out RIGHT
  // NOW. Both are refs for the reasons `useDayScreen` gives: StrictMode runs an
  // effect twice inside one commit, so a state flag saying "this is loading"
  // has not landed yet when the second run reads it.
  const monthLoadedIn = useRef<Map<MonthKey, Round>>(new Map());
  const monthInFlight = useRef<Set<MonthKey>>(new Set());
  const dayLoadedIn = useRef<Map<IsoDay, Round>>(new Map());
  const dayInFlight = useRef<Set<IsoDay>>(new Set());

  // WHICH TRIP TO THIS SCREEN THIS IS. Counted on the way OUT rather than on the
  // way in, so the number is fixed for the whole of a visit: a counter that moved
  // while the screen was open would be a read invalidating itself, and one bumped
  // as the screen arrives moves again under StrictMode's second run of the same
  // effect, which is the duplicate request the in-flight set exists to stop.
  //
  // A ref, and read only inside the effects below: it must not cause a render of
  // its own, and both effects re-run on `active` anyway, so they see it already
  // counted.
  const visit = useRef(0);

  useEffect(() => {
    if (active) return;
    visit.current += 1;
  }, [active]);

  const entry = byMonth[key];

  useEffect(() => {
    if (!active || monthInFlight.current.has(key)) return;
    const round: Round = { revision, completions, visit: visit.current };
    if (entry !== undefined && alreadyRead(monthLoadedIn.current.get(key), round)) return;

    monthInFlight.current.add(key);
    // Recorded on the way OUT rather than on the way back, so a read that failed
    // is a read this VISIT has had and the effect does not re-fire on it for
    // ever. Asking again without leaving is `retry`, which forgets the entry;
    // leaving and returning is a new visit, which this record does not cover.
    monthLoadedIn.current.set(key, round);
    // A month already READ stays on screen while it is replaced, because putting
    // "Loading…" over a grid that is right costs a flash of nothing for no
    // information. A month that FAILED has nothing worth keeping: leaving it up
    // would draw the last visit's reason over a request that is out right now,
    // under a "Try again" that is not what is happening.
    setByMonth((cache) =>
      cache[key]?.status === 'failed' || cache[key] === undefined
        ? { ...cache, [key]: { status: 'loading' } }
        : cache,
    );

    void (async () => {
      try {
        const payload = await api.month(key);
        const problem = contractProblem(payload, key);
        if (problem !== null) {
          setByMonth((cache) => withProblem(cache, key, problem));
          return;
        }
        const loaded: Entry<MonthResponse> = { status: 'loaded', payload };
        // AN ANSWER OUTLIVES THE VISIT THAT ASKED FOR IT, which is what makes
        // coming back to a month already read cost nothing. Filed under its own
        // name as well as under the literal, and both are as fresh as each other
        // - or coming back to this month by name would fetch it a second time.
        // The day screen files `today` the same way.
        const answered: Round = { revision, completions, visit: null };
        monthLoadedIn.current.set(key, answered);
        monthLoadedIn.current.set(payload.month, answered);
        if (parseIsoDay(payload.today) !== null) setToday(payload.today);
        setByMonth((cache) => ({ ...cache, [key]: loaded, [payload.month]: loaded }));
      } catch (error) {
        if (isSignedOut(error)) signedOut.current();
        // Filed against the entry even so, which is what stops this month being
        // asked for again: the effect re-fires for anything with no entry, and a
        // 401 that left the cache empty would send the same doomed request for
        // ever. What that means for a month already on screen is at
        // `withProblem`.
        setByMonth((cache) => withProblem(cache, key, messageFor(error)));
      } finally {
        monthInFlight.current.delete(key);
      }
    })();
  }, [active, key, entry, revision, completions]);

  const dayEntry = selected === null ? undefined : byDay[selected];

  useEffect(() => {
    const day = selected;
    if (!active || day === null || dayInFlight.current.has(day)) return;
    const round: Round = { revision, completions, visit: visit.current };
    if (dayEntry !== undefined && alreadyRead(dayLoadedIn.current.get(day), round)) return;

    dayInFlight.current.add(day);
    dayLoadedIn.current.set(day, round);
    setByDay((cache) =>
      cache[day]?.status === 'failed' || cache[day] === undefined
        ? { ...cache, [day]: { status: 'loading' } }
        : cache,
    );

    void (async () => {
      try {
        const payload = await api.day(day);
        const problem = dayContractProblem(payload, day);
        if (problem !== null) {
          setByDay((cache) => withProblem(cache, day, problem));
          return;
        }
        dayLoadedIn.current.set(day, { revision, completions, visit: null });
        setByDay((cache) => ({ ...cache, [day]: { status: 'loaded', payload } }));
      } catch (error) {
        if (isSignedOut(error)) signedOut.current();
        setByDay((cache) => withProblem(cache, day, messageFor(error)));
      } finally {
        dayInFlight.current.delete(day);
      }
    })();
  }, [active, selected, dayEntry, revision, completions]);

  const view = entry?.status === 'loaded' ? entry.payload : null;

  // The month being ASKED FOR, named even when its payload never arrived: the
  // payload's own name first, then the key, and the literal resolved through the
  // server's `today` rather than through anything on this machine.
  const month: IsoMonth | null =
    view?.month ?? (key === THIS_MONTH ? (today === null ? null : monthOfDay(today)) : key);

  const go = useCallback((month: MonthKey) => {
    setKey(month);
    // A day belongs to the month it is in. Left open, a square from July would
    // sit under an August grid with nothing on that grid marked as the open one
    // - a panel about a day the screen is no longer showing.
    setSelected(null);
  }, []);

  const forgetMonth = useCallback((month: MonthKey) => {
    setByMonth((cache) => {
      if (cache[month] === undefined) return cache;
      const next = { ...cache };
      delete next[month];
      return next;
    });
  }, []);

  // Forgetting is what asks again: the effect above sends a request for exactly
  // the key that has no entry.
  const retry = useCallback(() => {
    forgetMonth(key);
  }, [forgetMonth, key]);

  const forgetDay = useCallback((day: IsoDay) => {
    setByDay((cache) => {
      if (cache[day] === undefined) return cache;
      const next = { ...cache };
      delete next[day];
      return next;
    });
  }, []);

  const select = useCallback(
    (day: IsoDay | null) => {
      const opening = day !== null && day !== selected;
      // OPENING A SQUARE THAT COULD NOT BE READ ASKS AGAIN. A failure parks
      // within the visit so the effect does not re-fire on its own, and pressing
      // the square is the one thing that says somebody wants it now - a control
      // that hands back the same reason with no request is a control that
      // appears to do nothing, which is the trap this client keeps meeting. A
      // day already READ is left alone, or every open would cost a round trip.
      if (opening && byDay[day]?.status === 'failed') forgetDay(day);
      setSelected(opening ? day : null);
    },
    [byDay, forgetDay, selected],
  );

  const retryDay = useCallback(() => {
    if (selected !== null) forgetDay(selected);
  }, [forgetDay, selected]);

  return useMemo(
    () => ({
      view,
      month,
      previousMonth: view?.previousMonth ?? (month === null ? null : addMonths(month, -1)),
      nextMonth: view?.nextMonth ?? (month === null ? null : addMonths(month, 1)),
      thisMonth: today === null ? null : monthOfDay(today),
      loading: entry === undefined || entry.status === 'loading',
      failure: entry?.status === 'failed' ? entry.message : null,
      refreshFailure: entry?.status === 'loaded' ? (entry.refreshFailure ?? null) : null,
      go,
      retry,
      selected,
      select,
      day: dayEntry?.status === 'loaded' ? dayEntry.payload : null,
      dayLoading: selected !== null && (dayEntry === undefined || dayEntry.status === 'loading'),
      dayFailure: dayEntry?.status === 'failed' ? dayEntry.message : null,
      dayRefreshFailure: dayEntry?.status === 'loaded' ? (dayEntry.refreshFailure ?? null) : null,
      retryDay,
    }),
    [view, month, today, entry, go, retry, selected, select, dayEntry, retryDay],
  );
}
